import { useQuery } from '@tanstack/react-query';
import { rpc } from '@/lib/explorer/useExplorerData';

/**
 * The Still Remote line in the job page's company card. For employers listed on
 * stillremote.com (companies whose own job boards show at least 60% of open roles
 * remote), one anon RPC, still_remote_company, returns the remote and total open
 * roles from the directory's latest daily read, matched on af_company_key. The
 * company's Still Remote page shows the same numbers to every visitor. Renders
 * nothing for employers that are not listed, or when the read is over 7 days old.
 */

interface StillRemoteCompany {
  slug: string;
  name: string;
  board_remote: number;
  board_total: number;
  remote_pct: number | null;
  checked_at: string | null;
}

export function StillRemoteFact({ company }: { company: string }) {
  const { data } = useQuery({
    queryKey: ['still_remote_company', company],
    queryFn: async () => {
      const { data, error } = await rpc('still_remote_company', { p_company: company });
      if (error) throw new Error(error.message);
      return (data ?? null) as StillRemoteCompany | null;
    },
    enabled: !!company,
    staleTime: 1000 * 60 * 30,
  });

  if (!data || !data.slug || !data.board_total) return null;

  const checked = data.checked_at
    ? new Date(data.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
  const href =
    `https://stillremote.com/companies/${encodeURIComponent(data.slug)}` +
    '?utm_source=auditfriendly&utm_medium=job_page&utm_campaign=company_card';

  return (
    <div className="company-card__remote">
      <span className="company-card__remote-label">Remote-first employer</span>
      <p className="company-card__remote-line">
        <b>{data.board_remote.toLocaleString('en-US')}</b> of {data.board_total.toLocaleString('en-US')} open roles
        at {company} are remote{checked ? `, read from its own job board on ${checked}` : ''}.{' '}
        <a href={href} target="_blank" rel="noopener noreferrer">
          See {company} on Still Remote ↗
        </a>
      </p>
    </div>
  );
}
